import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Button,
  Flex,
  Text,
  Icon,
  Box,
  useColorModeValue,
  useDisclosure,
  useToast,
  IconButton,
} from "@chakra-ui/react";
import { useHistory, useLocation } from "react-router-dom";
import { FaStar, FaBrain, FaUsers, FaChartLine, FaTasks, FaExclamationTriangle } from "react-icons/fa";
import { GiStoneBlock } from "react-icons/gi";
import { FiSettings, FiChevronDown, FiCalendar, FiFileText, FiBell } from "react-icons/fi";
import { AiFillDashboard } from "react-icons/ai";
import { useAuth } from "../../contexts/AuthContext";
import { useWinsExcited } from "../../pages/EOS/WinsExcited/useWinsExcited";
import { useIdeas } from "../../pages/EOS/Ideas/useIdeas";
import { WinExcitedModal } from "../../pages/EOS/Rocks/components/WinExcitedModal";
import { AddIdeaModal } from "../../pages/EOS/Ideas/components/AddIdeaModal";
import { AddIssueModal } from "../../pages/EOS/Issues/components/AddIssueModal";
import { useIssues } from "../../pages/EOS/Issues/useIssues";
import { useDepartments } from "../../pages/EOS/Departments/useDepartments";

export default function EOSMenu() {
  const history = useHistory();
  const location = useLocation();
  const { SuperAdmin, currentUser } = useAuth();
  const toast = useToast();
  
  // Wins/Excited modal
  const { isOpen: isWinExcitedOpen, onOpen: onWinExcitedOpen, onClose: onWinExcitedClose } = useDisclosure();
  const { addWinExcited, users: winsExcitedUsers } = useWinsExcited(currentUser?.uid);
  
  // Ideas modal
  const { isOpen: isIdeaOpen, onOpen: onIdeaOpen, onClose: onIdeaClose } = useDisclosure();
  const { createIdea } = useIdeas(currentUser?.uid);
  
  // Company Issue modal
  const { isOpen: isCompanyIssueModalOpen, onOpen: onCompanyIssueModalOpen, onClose: onCompanyIssueModalClose } = useDisclosure();
  const { createIssue, users } = useIssues(currentUser?.uid);
  const { departments } = useDepartments();
  
  const activeBg = useColorModeValue("#e8e8e8", "gray.700");
  const textColor = useColorModeValue("blue.600", "blue.400");
  const hoverBg = useColorModeValue("gray.100", "gray.600");

  const handleNavigation = (path) => {
    history.push(path);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const handleAddWinExcited = async (type, content, taggedUserId, currentUserId) => {
    try {
      await addWinExcited(type, content, taggedUserId, currentUserId);
      toast({
        title: 'Success',
        description: `${type === 'win' ? 'Win' : 'Excitement'} shared!`,
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      onWinExcitedClose();
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleCreateIdea = async (values) => {
    try {
      await createIdea(values);
      toast({
        title: 'Idea created',
        status: 'success',
        duration: 3000,
      });
      onIdeaClose();
    } catch (error) {
      toast({
        title: 'Error creating idea',
        description: error instanceof Error ? error.message : 'Failed to create idea',
        status: 'error',
        duration: 5000,
      });
      throw error;
    }
  };

  const handleSaveCompanyIssue = async (formData) => {
    try {
      await createIssue({
        ...formData,
        isCompany: true,
      });
      toast({
        title: 'Company issue created',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      onCompanyIssueModalClose();
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to create company issue',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Goals & Tracking menu items
  const goalsAndTrackingItems = [
    { icon: GiStoneBlock, name: "All Rocks", to: "/eos/rocks" },
    { icon: FaChartLine, name: "Scorecard", to: "/eos/scorecard" },
    { icon: FaTasks, name: "Action Items", to: "/eos/action-items" },
    { icon: FiCalendar, name: "Calendar", to: "/eos/calendar" },
    { icon: FaExclamationTriangle, name: "Company Issues", to: "/eos/issues" },
  ];

  // Meetings menu items
  const meetingsItems = [
    { icon: FaUsers, name: "L10 Meeting", to: "/eos/l10" },
    { icon: FiFileText, name: "Meeting Summaries", to: "/eos/meeting-summaries" },
    { icon: FiFileText, name: "Activity Summaries", to: "/eos/weekly-summary" },
    { icon: FaStar, name: "Wins / Excited", to: "/eos/wins-excited" },
  ];

  // Tools menu items
  const toolsItems = [
    { icon: FiFileText, name: "V/TO", to: "/eos/vto" },
    { icon: FaUsers, name: "Accountability Chart", to: "/eos/accountability-chart" },
    { icon: FaBrain, name: "Ideas", to: "/eos/ideas" },
        { icon: FiBell, name: "Notifications", to: "/eos/notifications" },

  ];

  // Add Admin if SuperAdmin
  if (SuperAdmin) {
    toolsItems.push({ icon: FiSettings, name: "Admin", to: "/eos/admin" });
  }

  // Combined EOS menu items (for single menu on sm-md screens)
  const allEOSItems = [
    ...goalsAndTrackingItems.map(item => ({ ...item, section: "goals" })),
    ...meetingsItems.map(item => ({ ...item, section: "meetings" })),
    ...toolsItems.map(item => ({ ...item, section: "tools" })),
  ];

  // Check if any EOS route is active
  const isEOSRouteActive = location.pathname.startsWith("/eos/") && 
    !location.pathname.startsWith("/eos/dashboard");

  return (
    <>
      <Flex alignItems="center" gap={4} width="100%" justifyContent="space-between" h="100%">
        <Flex alignItems="center" gap={4} h="100%">
          {/* Dashboard Button */}
          <Button
            leftIcon={<Icon as={AiFillDashboard} />}
            onClick={() => handleNavigation("/eos/dashboard")}
            bg={isActive("/eos/dashboard") ? activeBg : "transparent"}
            color={isActive("/eos/dashboard") ? textColor : "gray.700"}
            _hover={{ bg: hoverBg }}
            fontWeight="medium"
            size="sm"
            h="32px"
          >
            Dashboard
          </Button>

          {/* Single EOS Menu - only show on sm-md screens */}
          <Menu>
            <MenuButton
              as={Box}
              px={3}
              py={1}
              borderRadius="md"
              h="32px"
              display={{ base: "none", sm: "flex", lg: "none" }}
              alignItems="center"
              bg={isEOSRouteActive ? activeBg : "transparent"}
              color={isEOSRouteActive ? textColor : "gray.700"}
              _hover={{ bg: hoverBg }}
              cursor="pointer"
              fontWeight="medium"
            >
            <Flex alignItems="center" gap={2}>
              <Icon as={GiStoneBlock} />
              <Text>EOS</Text>
              <Icon as={FiChevronDown} fontSize="sm" />
            </Flex>
            </MenuButton>
            <MenuList zIndex={100}>
              {allEOSItems.map((item, index) => {
                // Add divider before Meetings section (first item of meetings)
                const showDividerBefore = index > 0 && 
                  item.section === "meetings" && 
                  allEOSItems[index - 1].section === "goals";
                // Add divider before Tools section (first item of tools)
                const showDividerBeforeTools = index > 0 &&
                  item.section === "tools" &&
                  allEOSItems[index - 1].section === "meetings";
                
                return (
                  <Box key={item.to}>
                    {(showDividerBefore || showDividerBeforeTools) && <MenuDivider />}
                    <MenuItem
                      onClick={() => handleNavigation(item.to)}
                      icon={<Icon as={item.icon} />}
                      bg={isActive(item.to) ? activeBg : "transparent"}
                    >
                      {item.name}
                    </MenuItem>
                  </Box>
                );
              })}
            </MenuList>
          </Menu>

          {/* Goals & Tracking Dropdown - only show on lg+ screens */}
          <Menu>
            <MenuButton
              as={Box}
              px={3}
              py={1}
              borderRadius="md"
              h="32px"
              display={{ base: "none", lg: "flex" }}
              alignItems="center"
              bg={location.pathname.startsWith("/eos/rocks") || 
                  location.pathname.startsWith("/eos/scorecard") ||
                  location.pathname.startsWith("/eos/action-items") ||
                  location.pathname.startsWith("/eos/calendar") ||
                  location.pathname.startsWith("/eos/issues") ? activeBg : "transparent"}
              color={location.pathname.startsWith("/eos/rocks") || 
                     location.pathname.startsWith("/eos/scorecard") ||
                     location.pathname.startsWith("/eos/action-items") ||
                     location.pathname.startsWith("/eos/calendar") ||
                     location.pathname.startsWith("/eos/issues") ? textColor : "gray.700"}
              _hover={{ bg: hoverBg }}
              cursor="pointer"
              fontWeight="medium"
            >
            <Flex alignItems="center" gap={2}>
              <Icon as={GiStoneBlock} />
              <Text display={{ base: "none", xl: "block" }}>Goals & Tracking</Text>
              <Text display={{ base: "block", xl: "none" }}>Goals</Text>
              <Icon as={FiChevronDown} fontSize="sm" />
            </Flex>
            </MenuButton>
            <MenuList zIndex={100}>
              {goalsAndTrackingItems.map((item) => (
                <MenuItem
                  key={item.to}
                  onClick={() => handleNavigation(item.to)}
                  icon={<Icon as={item.icon} />}
                  bg={isActive(item.to) ? activeBg : "transparent"}
                >
                  {item.name}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>

          {/* Meetings Dropdown - only show on lg+ screens */}
          <Menu>
            <MenuButton
              as={Box}
              px={3}
              py={1}
              borderRadius="md"
              h="32px"
              display={{ base: "none", lg: "flex" }}
              alignItems="center"
              bg={location.pathname.startsWith("/eos/l10") || 
                  location.pathname.startsWith("/eos/meeting-summaries") ||
                  location.pathname.startsWith("/eos/weekly-summary") ||
                  location.pathname.startsWith("/eos/wins-excited") ||
                  location.pathname.startsWith("/eos/notifications") ? activeBg : "transparent"}
              color={location.pathname.startsWith("/eos/l10") || 
                     location.pathname.startsWith("/eos/meeting-summaries") ||
                     location.pathname.startsWith("/eos/weekly-summary") ||
                     location.pathname.startsWith("/eos/wins-excited") ||
                     location.pathname.startsWith("/eos/notifications") ? textColor : "gray.700"}
              _hover={{ bg: hoverBg }}
              cursor="pointer"
              fontWeight="medium"
            >
            <Flex alignItems="center" gap={2}>
              <Icon as={FaUsers} />
              <Text>Meetings</Text>
              <Icon as={FiChevronDown} fontSize="sm" />
            </Flex>
            </MenuButton>
            <MenuList zIndex={100}>
              {meetingsItems.map((item) => (
                <MenuItem
                  key={item.to}
                  onClick={() => handleNavigation(item.to)}
                  icon={<Icon as={item.icon} />}
                  bg={isActive(item.to) ? activeBg : "transparent"}
                >
                  {item.name}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>

          {/* Tools Dropdown - only show on lg+ screens */}
          <Menu>
            <MenuButton
              as={Box}
              px={3}
              py={1}
              borderRadius="md"
              h="32px"
              display={{ base: "none", lg: "flex" }}
              alignItems="center"
              bg={location.pathname.startsWith("/eos/vto") || 
                  location.pathname.startsWith("/eos/accountability-chart") ||
                  location.pathname.startsWith("/eos/ideas") ||
                  (SuperAdmin && location.pathname.startsWith("/eos/admin")) ? activeBg : "transparent"}
              color={location.pathname.startsWith("/eos/vto") || 
                     location.pathname.startsWith("/eos/accountability-chart") ||
                     location.pathname.startsWith("/eos/ideas") ||
                     (SuperAdmin && location.pathname.startsWith("/eos/admin")) ? textColor : "gray.700"}
              _hover={{ bg: hoverBg }}
              cursor="pointer"
              fontWeight="medium"
            >
            <Flex alignItems="center" gap={2}>
              <Icon as={FiSettings} />
              <Text>Tools</Text>
              <Icon as={FiChevronDown} fontSize="sm" />
            </Flex>
            </MenuButton>
            <MenuList zIndex={100}>
              {toolsItems.map((item) => (
                <MenuItem
                  key={item.to}
                  onClick={() => handleNavigation(item.to)}
                  icon={<Icon as={item.icon} />}
                  bg={isActive(item.to) ? activeBg : "transparent"}
                >
                  {item.name}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        </Flex>

        {/* Right side buttons */}
        <Flex alignItems="center" gap={{ base: 1, md: 2, lg: 3 }} h="100%">
          {/* Wins/Excited Button */}
          <Button
            leftIcon={<Icon as={FaStar} />}
            colorScheme="blue"
            variant="solid"
            size="sm"
            h="32px"
            onClick={onWinExcitedOpen}
            display={{ base: "none", xl: "flex" }}
          >
            Add Win / Excited
          </Button>
          <IconButton
            aria-label="Add Win / Excited"
            icon={<Icon as={FaStar} />}
            colorScheme="blue"
            variant="solid"
            size="sm"
            h="32px"
            onClick={onWinExcitedOpen}
            display={{ base: "flex", xl: "none" }}
          />

          {/* Ideas Button */}
          <Button
            leftIcon={<Icon as={FaBrain} />}
            colorScheme="purple"
            variant="solid"
            size="sm"
            h="32px"
            onClick={onIdeaOpen}
            display={{ base: "none", xl: "flex" }}
          >
            Ideas / Feedback
          </Button>
          <IconButton
            aria-label="Ideas / Feedback"
            icon={<Icon as={FaBrain} />}
            colorScheme="purple"
            variant="solid"
            size="sm"
            h="32px"
            onClick={onIdeaOpen}
            display={{ base: "flex", xl: "none" }}
          />

          {/* Company Issue Button */}
          <Button
            leftIcon={<Icon as={FaExclamationTriangle} />}
            colorScheme="orange"
            variant="solid"
            size="sm"
            h="32px"
            onClick={onCompanyIssueModalOpen}
            display={{ base: "none", xl: "flex" }}
          >
            Company Issue
          </Button>
          <IconButton
            aria-label="Company Issue"
            icon={<Icon as={FaExclamationTriangle} />}
            colorScheme="orange"
            variant="solid"
            size="sm"
            h="32px"
            onClick={onCompanyIssueModalOpen}
            display={{ base: "flex", xl: "none" }}
          />
        </Flex>
      </Flex>

      {/* Modals */}
      <WinExcitedModal
        isOpen={isWinExcitedOpen}
        onClose={onWinExcitedClose}
        currentUserId={currentUser?.uid}
        users={winsExcitedUsers}
        onSave={handleAddWinExcited}
      />

      <AddIdeaModal
        isOpen={isIdeaOpen}
        onClose={onIdeaClose}
        onSubmit={handleCreateIdea}
      />

      <AddIssueModal
        isOpen={isCompanyIssueModalOpen}
        onClose={onCompanyIssueModalClose}
        onSave={handleSaveCompanyIssue}
        currentUserId={currentUser?.uid}
        isCompany={true}
        modalTitle="Add Company Issue"
        submitButtonText="Add Company Issue"
        users={users}
        departments={departments}
      />
    </>
  );
}

